import { context, Span, SpanOptions, trace, Tracer } from "@opentelemetry/api";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { Resource } from "@opentelemetry/resources";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";

// prevent console errors from Promise.reject()
// reference: https://github.com/NativeScript/nativescript-angular/issues/2278
Zone[Zone.__symbol__("ignoreConsoleErrorUncaughtError")] = true;

type OTelConfig = {
	otelCollectorUrl: string;
}

type CSIConfig = {
	Date: string;
	Branch: string;
	Product: string;
	Version: string;
}

declare var otelConfig: OTelConfig;

declare var csiConfig: CSIConfig;

const SERVICE_NAME = "C8-web-client";

/**
 * Initializes OpenTelemetry instrumentation.
 *
 * URL of OTLP Connector needs to be specified in csiConfig.js in 'otelConfig' global variable.
 * To do it in Docker environment, set C8_APPSERVER_OTEL_URL_FOR_CLIENT environment variable. Example:
 * 		C8_APPSERVER_OTEL_URL_FOR_CLIENT=http://your-server:4318/v1/traces
 *
 * Also, you need to enable connection to OTLP Connector from the browser using Content-Security-Policy header
 * To do it in Docker environment, set C8_APPSERVER_CSP_CONNECT_SRC_EXTRA environment variable. Example:
 * 		C8_APPSERVER_CSP_CONNECT_SRC_EXTRA=http://your-server:4318
 */
export function init(): void {
	const otelCollectorUrl = otelConfig.otelCollectorUrl;
	if (!otelCollectorUrl) {
		return;
	}

	const exporter = new OTLPTraceExporter({
		url: otelCollectorUrl,
	});

	const resource =
		Resource.default().merge(new Resource({
			[SemanticResourceAttributes.SERVICE_NAME]: SERVICE_NAME,
			[SemanticResourceAttributes.SERVICE_VERSION]: csiConfig.Version,
		}));

	const provider = new WebTracerProvider({ resource });
	//provider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
	provider.addSpanProcessor(new BatchSpanProcessor(exporter));
	provider.register({
		contextManager: new ZoneContextManager(),
		propagator: new W3CTraceContextPropagator(),
	});

	registerInstrumentations({
		instrumentations: [
			new XMLHttpRequestInstrumentation({ clearTimingResources: true }),
			new FetchInstrumentation({ clearTimingResources: true }),
		],
	});
}

export function getTracer(): Tracer {
	return trace.getTracer(SERVICE_NAME);
}

export function getActiveSpan(): Span {
	return trace.getActiveSpan();
}

/**
 * Create a new span
 *
 * @param name - name of the span
 * @param options - options of the span
 * @returns - newly created span
 */
export function startSpan(name: string, options?: SpanOptions): Span {
	return getTracer().startSpan(name, options, context.active());
}

/**
 * Execute a function with an active context
 *
 * @param span
 * @param fn
 * @returns
 */
export function executeInContext<A extends unknown[], F extends (...args: A) => ReturnType<F>>(span: Span, fn: F): ReturnType<F> {
	return context.with(trace.setSpan(context.active(), span), fn);
}

/**
 * Bind an active context to a target function or event emitter
 *
 * @param target function or event emitter to bind
 */
export function bindToActiveContext<T>(target: T): T {
	if (target == null) {
		return target;
	}
	return context.bind(undefined, target);
}
